import { CalendarIcon, ChevronUpIcon, Cross2Icon } from '@radix-ui/react-icons';
import { Popover, TextField } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonDateWidget } from 'components/common/form/date/date-widget';
import {
  CommonTimeWidget,
  NEAREST_MINUTE,
} from 'components/common/form/date/time-widget';
import { CommonFormGrid } from 'components/common/form/grid';
import { CommonInputHint } from 'components/common/form/hint';
import { CommonInputLabel } from 'components/common/form/label';
import { CommonInputWrapper } from 'components/common/form/wrapper';
import { addHours, startOfDay } from 'date-fns';
import { format } from 'date-fns-tz';
import enCA from 'date-fns/locale/en-CA';
import { t } from 'i18next';
import { getTimeFromDate, IDateInput } from 'interfaces/forms/date';
import {
  LOCAL_DATE_FORMAT,
  LOCAL_DATETIME_FORMAT,
  LOCAL_DATETIME_TIMEZONE_FORMAT,
} from 'lib_ts/enums/datetime.enums';
import { clone } from 'lodash';
import { useEffect, useMemo, useRef, useState } from 'react';

const SHOW_TIMEZONE = true;

// TODO: Make this a controlled component
export const CommonDateInput = (props: IDateInput) => {
  const trigger = useRef<HTMLInputElement>(null);

  const [open, setOpen] = useState(false);
  const [selectedDate, setSelectedDate] = useState(props.defaultValue);

  // keep selected up to date w/ default value if it changes (e.g. switching machines and last hw changed date is different)
  useEffect(() => {
    setSelectedDate(props.defaultValue);
    props.onChange(props.defaultValue);
  }, [props.defaultValue?.toISOString()]);

  const changeSelectedDate = (value: Date | undefined) => {
    setSelectedDate(value);
    props.onChange(value);
  };

  const displayValue = useMemo(() => {
    if (!selectedDate) {
      return '';
    }

    if (props.displayFormat) {
      return format(selectedDate, props.displayFormat, {
        locale: enCA,
      });
    }

    if (props.showTime && SHOW_TIMEZONE) {
      // full, with timezone; locale is necessary to ensure TZ prints as the abbreviations instead of GMT[+/-]#
      return format(selectedDate, LOCAL_DATETIME_TIMEZONE_FORMAT, {
        locale: enCA,
      });
    }

    if (props.showTime) {
      // only time, no timezone
      return format(selectedDate, LOCAL_DATETIME_FORMAT);
    }

    // only date, no time or timezone
    return format(selectedDate, LOCAL_DATE_FORMAT);
  }, [selectedDate, props.displayFormat, props.showTime]);

  const triggerSlot = () => {
    if (props.optional && selectedDate) {
      return (
        <Cross2Icon
          onClick={() => {
            if (props.disabled) {
              return;
            }

            changeSelectedDate(undefined);
          }}
        />
      );
    }

    if (open) {
      return <ChevronUpIcon />;
    }

    return (
      <CalendarIcon
        onClick={() => {
          if (props.disabled) {
            return;
          }

          setOpen(true);
        }}
      />
    );
  };

  return (
    <CommonInputWrapper {...props}>
      <CommonInputLabel {...props} />

      <Popover.Root
        open={open}
        onOpenChange={(value) => {
          if (props.disabled) {
            return;
          }

          setOpen(value);
        }}
      >
        <Popover.Trigger disabled={props.disabled}>
          <TextField.Root
            ref={trigger}
            size={props.size}
            className={`cursor-pointer ${props.className ?? ''}`}
            disabled={props.disabled}
            color={props.inputColor}
            name={props.name}
            placeholder={t(
              props.placeholder ?? 'common.select-date-placeholder'
            ).toString()}
            required={!props.optional}
            value={displayValue}
            onChange={() => {
              // onChange provided to suppress error re: value w/o onChange
            }}
            onFocus={() => trigger?.current?.blur()}
            type="text"
            autoCapitalize="off"
            autoCorrect="off"
            autoComplete="off"
          >
            <TextField.Slot side="right">{triggerSlot()}</TextField.Slot>
          </TextField.Root>
        </Popover.Trigger>
        <Popover.Content className="BorderedContent">
          <CommonFormGrid columns={1}>
            <CommonDateWidget
              minDate={props.minDate}
              maxDate={props.maxDate}
              allowedDates={props.allowedDates}
              defaultDate={props.defaultValue}
              onChangeDate={(v) => {
                if (!v) {
                  changeSelectedDate(undefined);
                  return;
                }

                // leaving any time values alone
                const nextValue = clone(selectedDate ?? v);

                nextValue.setFullYear(v.getFullYear());
                nextValue.setMonth(v.getMonth());
                nextValue.setDate(v.getDate());

                changeSelectedDate(nextValue);
              }}
            />

            {/* hide time widget until we've selected a date */}
            {props.showTime && selectedDate && (
              <CommonTimeWidget
                defaultTime={
                  props.defaultValue
                    ? getTimeFromDate(props.defaultValue, NEAREST_MINUTE)
                    : undefined
                }
                onChangeTime={(v) => {
                  if (!selectedDate) {
                    NotifyHelper.warning({
                      message_md: `Please select a date first.`,
                      debug: props.skipSelectDateWarning,
                    });
                    return;
                  }

                  if (!v) {
                    changeSelectedDate(startOfDay(selectedDate));
                    return;
                  }

                  // reset all other time values to 0 before setting H and M
                  const raw = startOfDay(selectedDate);

                  raw.setHours(v.h);
                  raw.setMinutes(v.m);

                  // convert from minutes to negative hours
                  const local = -(new Date().getTimezoneOffset() / 60);
                  const delta = local - v.offset;

                  const adjusted = addHours(raw, delta);

                  changeSelectedDate(adjusted);
                }}
              />
            )}
          </CommonFormGrid>
        </Popover.Content>
      </Popover.Root>

      <CommonInputHint {...props} />
    </CommonInputWrapper>
  );
};
